import { DatePickerDemo } from '@alphabait/ui/components/ui/date-picker'
import { FormProps } from '@alphabait/ui/form'
import { useState } from 'react'
import { Control, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { AlphaTypes, CustomDateInput } from './custom-types'
import styles from './form.module.css'

// Legacy props (form module)
type LegacyProps = {
  field: CustomDateInput
  formProps: FormProps
}

// Direct props (react-hook-form)
type DirectProps = {
  name: string
  control: Control<any>
  label?: string
  testId?: string
  minDate?: Date
  maxDate?: Date
  className?: string
}

type CustomDateProps = LegacyProps | DirectProps

function isLegacyProps(props: CustomDateProps): props is LegacyProps {
  return 'field' in props && 'formProps' in props
}

// Export the component directly similar to CustomShadcnSelectComponent
export function CustomDateComponent(props: CustomDateProps) {
  // Extract props based on mode
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const testId = isLegacyProps(props) ? props.field.name : props.testId || props.name
  const minDate = isLegacyProps(props) ? props.field.minDate : props.minDate
  const maxDate = isLegacyProps(props) ? props.field.maxDate : props.maxDate
  const className = isLegacyProps(props)
    ? 'date-picker-container !text-foreground'
    : props.className || 'date-picker-container !text-foreground'

  // Legacy mode uses formValues for initial state
  const [dateValue, setDateValue] = useState<string | undefined>(
    isLegacyProps(props) ? props.formProps.formValues[fieldName] : undefined,
  )

  const {
    field: { onChange, ref, value },
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
  })

  const { t } = useTranslation()

  return (
    <div>
      <div data-testid={testId} className={className} ref={ref}>
        <DatePickerDemo
          value={dateValue || value}
          setValue={(d) => {
            setDateValue(d)
            onChange(d.substring(0, 10))
          }}
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>
      {error && (
        <span
          className={styles.errorMessage}
          data-testid={`${testId}-error`}
        >
          {t(`validation_messages.${error.message}`)}
        </span>
      )}
    </div>
  )
}

export const getCustomDateProperties = () =>
  [
    {
      name: 'uiOptions.label',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.question'),
        inputType: 'text',
      },
      validations: ['required'],
    },
    {
      name: 'validation.required',
      customControlType: 'switch',
      type: 'custom',
      uiOptions: {
        label: i18n.t('questionnaire_builder.required'),
      },
    },
  ] as AlphaTypes[]
